import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';
import TeaserImages from '../Helpers/TeaserImages';
import RelatedItemsDisplay from '../Helpers/RelatedItemsDisplay';
import AuthorsLink from '../Helpers/AuthorsLink';
import Sections from '../Helpers/Sections';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import { toDisplayFileUrl } from '../../../helpers/fileUrls';
import { hasImage, vocabTitle } from '../../../helpers/content';
import { flattenRichTextToAppURL } from '../../../helpers/richText';
import { formatDate } from '../../../helpers/dates';

const hasText = (field) => field?.data && field.data !== '<p></p>';
/**
 * A thesis at any stage: an open topic offered to students, one a student
 * is working on, or a completed one in the archive.
 *
 * An open topic is an offer, so it shows what the work is about and who to
 * ask; the student, the dates and the documents appear once it is assigned.
 */
const ThesisView = (props) => {
  const { content } = props;
  const status =
    (typeof content.thesis_status === 'object'
      ? content.thesis_status?.token
      : content.thesis_status) || 'open';
  const assigned = status !== 'open';
  const statusLabel =
    status === 'completed' ? null : vocabTitle(content.thesis_status) || null;

  const degreeLabel =
    content.thesis_degree?.length > 0 ? content.thesis_degree.join(', ') : null;

  const student = content.related_student?.['@id']
    ? [content.related_student]
    : content.thesis_author
      ? [{ title: content.thesis_author }]
      : [];
  const examiners = (content.related_examiners || []).filter(Boolean);
  const advisors = (content.related_advisors || []).filter(Boolean);

  const dates = assigned
    ? [
        ['Registration', content.thesis_start_date],
        ['Deadline', status === 'in_progress' && content.thesis_end_date],
        ['Submitted', content.thesis_submission_date],
        ['Final Presentation', content.thesis_presentation_date],
      ].filter(([, value]) => value)
    : [];

  // For an open topic the PDF describes the topic; later it is the thesis.
  const documents = (
    assigned
      ? [
          ['PDF', content.thesis_pdf_file],
          ['Presentation Slides', content.thesis_presentation_file],
        ]
      : [['Topic Description (PDF)', content.thesis_pdf_file]]
  ).filter(([, value]) => value);

  const image1 = hasImage(content.teaser_image1) ? content.teaser_image1 : null;
  const image2 = hasImage(content.teaser_image2) ? content.teaser_image2 : null;

  return (
    <Container id="view-wrapper" className="ThesisView">
      <div className="view-header">
        {(degreeLabel || statusLabel) && (
          <p className="view-header__subtitle">
            {[degreeLabel, statusLabel].filter(Boolean).join(' · ')}
          </p>
        )}
        <h2 className="view-header__title">{content.title || content.id}</h2>
        {hasText(content.thesis_description) && (
          <div
            className="view-header__description"
            dangerouslySetInnerHTML={{
              __html: flattenRichTextToAppURL(content.thesis_description.data),
            }}
          />
        )}
        <div className="view-header__divider" />
      </div>

      <Sections>
        {(image1 || image2) && <TeaserImages key="images" content={content} />}

        {assigned && (student.length > 0 || dates.length > 0) && (
          <div key="student" className="view-meta-grid">
            <div className="view-meta-grid__col">
              {student.length > 0 && (
                <div className="view-section">
                  <h3 className="view-section__label">Student:</h3>
                  <p className="view-section__value">
                    <AuthorsLink authors={student} inline fullNames />
                  </p>
                </div>
              )}
            </div>
            <div className="view-meta-grid__col">
              {dates.map(([label, value]) => (
                <div className="view-section" key={label}>
                  <h3 className="view-section__label">{label}:</h3>
                  <p className="view-section__value">{formatDate(value)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {(hasText(content.thesis_abstract) ||
          hasText(content.thesis_requirements) ||
          hasText(content.thesis_related_work)) && (
          <div key="texts">
            {[
              ['Abstract', content.thesis_abstract],
              ['Requirements', content.thesis_requirements],
              ['Related Work', content.thesis_related_work],
            ]
              .filter(([, field]) => hasText(field))
              .map(([label, field]) => (
                <div className="view-section" key={label}>
                  <h3 className="view-section__label">{label}:</h3>
                  <div
                    className="view-section__richtext"
                    dangerouslySetInnerHTML={{
                      __html: flattenRichTextToAppURL(field.data),
                    }}
                  />
                </div>
              ))}
          </div>
        )}

        {documents.length > 0 && (
          <div key="documents" className="view-section">
            <h3 className="view-section__label">Documents:</h3>
            <div className="view-section__buttons">
              {documents.map(([label, url]) => (
                <a
                  key={label}
                  href={toDisplayFileUrl(url)}
                  className="view-section__button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        )}

        {advisors.length > 0 && !content.hide_related_advisors && (
          <div key="advisors" className="view-section">
            <h3 className="view-section__label">Advisors:</h3>
            <RelatedItemsDisplay
              items={advisors}
              mode={content.related_advisors_display}
              kind="person"
              fieldKey={`${content['@id']}-related_advisors`}
            />
          </div>
        )}

        {assigned && examiners.length > 0 && (
          <div key="examiners" className="view-section">
            <h3 className="view-section__label">Second Examiners:</h3>
            <p className="view-section__value">
              <AuthorsLink authors={examiners} fullNames />
            </p>
          </div>
        )}

        {content.related_topics?.length > 0 && !content.hide_related_topics && (
          <div key="topics" className="view-section">
            <h3 className="view-section__label">Topics:</h3>
            <RelatedItemsDisplay
              items={content.related_topics}
              mode={content.related_topics_display}
              kind="plain"
              fieldKey={`${content['@id']}-related_topics`}
            />
          </div>
        )}
      </Sections>

      <DefaultViewWithoutTitle {...props} />
    </Container>
  );
};

const personShape = PropTypes.shape({
  '@id': PropTypes.string,
  '@type': PropTypes.string,
  title: PropTypes.string,
  is_linkable: PropTypes.bool,
});

ThesisView.propTypes = {
  content: PropTypes.shape({
    '@id': PropTypes.string,
    title: PropTypes.string,
    id: PropTypes.string,
    thesis_status: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ token: PropTypes.string, title: PropTypes.string }),
    ]),
    thesis_degree: PropTypes.arrayOf(PropTypes.string),
    thesis_author: PropTypes.string,
    related_student: personShape,
    related_examiners: PropTypes.arrayOf(personShape),
    teaser_image1: PropTypes.object,
    teaser_image1_caption: PropTypes.string,
    teaser_image1_copyright: PropTypes.string,
    teaser_image1_copyright_black_font: PropTypes.bool,
    teaser_image2: PropTypes.object,
    teaser_image2_caption: PropTypes.string,
    teaser_image2_copyright: PropTypes.string,
    teaser_image2_copyright_black_font: PropTypes.bool,
    thesis_start_date: PropTypes.string,
    thesis_end_date: PropTypes.string,
    thesis_submission_date: PropTypes.string,
    thesis_presentation_date: PropTypes.string,
    thesis_description: PropTypes.shape({ data: PropTypes.string }),
    thesis_abstract: PropTypes.shape({ data: PropTypes.string }),
    thesis_requirements: PropTypes.shape({ data: PropTypes.string }),
    thesis_related_work: PropTypes.shape({ data: PropTypes.string }),
    thesis_pdf_file: PropTypes.string,
    thesis_presentation_file: PropTypes.string,
    hide_related_advisors: PropTypes.bool,
    related_advisors_display: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
    related_advisors: PropTypes.arrayOf(personShape),
    hide_related_topics: PropTypes.bool,
    related_topics_display: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
    related_topics: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
  }).isRequired,
};

export default ThesisView;
